// @systeme: interface
import { useState, type FormEvent } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link as LienRouteur, useSearchParams } from 'react-router';
import { FormulaireAuth } from '../composants/FormulaireAuth';
import { messageDe } from '../composants/Messages';
import { ErreurClient } from '../lib/api';
import { useSession } from '../session/SessionContext';

export function Inscription() {
  const { inscrire } = useSession();
  const [parametres] = useSearchParams();
  const invitation = parametres.get('invitation') ?? undefined;
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [cgu, setCgu] = useState(false);
  const [erreur, setErreur] = useState<string | undefined>();
  const [details, setDetails] = useState<Record<string, string[]>>({});
  const [enCours, setEnCours] = useState(false);

  async function soumettre(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErreur(undefined);
    setDetails({});
    setEnCours(true);
    try {
      if (!cgu) {
        setErreur('Il faut accepter les conditions pour continuer.');
        return;
      }
      await inscrire({ prenom, email, motDePasse, invitation, cguAcceptees: true });
    } catch (err) {
      setErreur(messageDe(err));
      if (err instanceof ErreurClient && err.details) setDetails(err.details);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <FormulaireAuth
      titre="Créer ton compte"
      sousTitre={
        invitation
          ? 'Tu rejoins le foyer qui t’a invitée : tout est déjà partagé avec lui.'
          : 'Un mois d’essai, sans carte bancaire. Tout ce que tu notes reste à toi.'
      }
      erreur={erreur}
      onSoumettre={soumettre}
      pied={
        <Link
          component={LienRouteur}
          to={invitation ? `/connexion?invitation=${invitation}` : '/connexion'}
        >
          Déjà un compte ? Me connecter
        </Link>
      }
    >
      <TextField
        label="Ton prénom"
        autoComplete="given-name"
        value={prenom}
        onChange={(e) => setPrenom(e.target.value)}
        required
        error={Boolean(details['prenom'])}
        helperText={details['prenom']?.[0]}
      />
      <TextField
        label="Ton adresse courriel"
        type="email"
        autoComplete="email"
        inputMode="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        error={Boolean(details['email'])}
        helperText={details['email']?.[0]}
      />
      <TextField
        label="Un mot de passe (8 caractères ou plus)"
        type="password"
        autoComplete="new-password"
        value={motDePasse}
        onChange={(e) => setMotDePasse(e.target.value)}
        required
        error={Boolean(details['motDePasse'])}
        helperText={details['motDePasse']?.[0]}
      />
      <FormControlLabel
        control={<Checkbox checked={cgu} onChange={(e) => setCgu(e.target.checked)} />}
        label={
          <span>
            J’ai lu et j’accepte les{' '}
            <LienRouteur to="/juridique/cgu" target="_blank" style={{ color: 'inherit' }}>
              conditions d’utilisation
            </LienRouteur>{' '}
            et la{' '}
            <LienRouteur
              to="/juridique/confidentialite"
              target="_blank"
              style={{ color: 'inherit' }}
            >
              politique de confidentialité
            </LienRouteur>
            .
          </span>
        }
      />
      <Button type="submit" variant="contained" size="large" disabled={enCours}>
        C’est parti
      </Button>
      <Typography variant="body2" color="text.secondary">
        Pas de rappels sur le téléphone sans l’installer sur l’écran d’accueil : on t’expliquera
        comment, c’est deux gestes.
      </Typography>
    </FormulaireAuth>
  );
}
